// === INBOEDEL — payload construction, response merge, defaults, schema cleanup ===
// Pure functions extracted verbatim from InboedelScreen.tsx. Impure inputs are injected
// via opts (appName/baseUrl/now) so the full BFF payload chain is deterministic and
// golden-testable offline (see calc.test.ts). Mirrors aansprakelijkheid/calc.ts.
import { buildGlassRequestBlock, GLASS_MYAAND, readInboedelGlassAmount } from '../../../utils/glassPremium';
import { getCurrentLocale } from '../../../utils/currency';
import { EC_CODE_MOBIEL, EC_CODE_SMARTHOME, EC_DEFAULT_SUM, getEc, selectedEcCode } from './coverages';

/** PP.BA fields the inboedel schema allows (additionalProperties: false). */
export const INBOEDEL_ALLOWED_BA_FIELDS = new Set([
  'BA_AARDBW', 'BA_AARDDAK', 'BA_BDRFBEL', 'BA_BOUWJR', 'BA_EIGENWO', 'BA_HERBVST',
  'BA_HERBWRD', 'BA_HUISNR', 'BA_MONUMEN', 'BA_OPPWON', 'BA_OPZPKVA', 'BA_PCODE',
  'BA_PERMBEW', 'BA_PLAATS', 'BA_RIETDAK', 'BA_SRTRDAK', 'BA_STRAAT', 'BA_TOEVOEG',
  'BA_TYPEWON', 'BA_VOLGNUM', 'BA_VRWRKCD', 'BA_WONING',
]);

export const CARRIER_CODE = 'R515';
export const PRODUCT_NAME = 'Inboedel_2024';

const EC_PREMIUM_ECHO_FIELDS = ['EC_BTP', 'EC_NTP', 'EC_TASS', 'EC_TPP', 'EC_TTOT', 'EC_VRWRKCD', 'EC_WPREMBP'] as const;

export const VP_ADDRESS_UI_FIELDS = ['VP_PCODE', 'VP_HUISNR', 'VP_TOEVOEG', 'VP_STRAAT', 'VP_PLAATS'] as const;
const carryEcPremiumEcho = (entry: any, src: any): any => {
  for (const f of EC_PREMIUM_ECHO_FIELDS) {
    if (src?.[f] !== undefined) entry[f] = src[f];
  }
  return entry;
};

export interface ApplyInboedelDefaultsOptions {
  appName: string;
  baseUrl: string;
  now: Date;
}

export const applyInboedelDiscount = (
  pp: any,
  discountCfg: { enabled: boolean; percentage: number; excludeCoverages: string[] }
) => {
  if (!pp || !discountCfg.enabled || discountCfg.percentage <= 0) return;
  const factor = 1 - discountCfg.percentage / 100;
  const excluded = discountCfg.excludeCoverages;
  // Keep full precision — downstream math (× 0.8 provision strip × 1.21 tax → floor) needs
  // the discounted BTP at ~4 decimals to land on the correct cent. Mirrors AVP's bake: BFF
  // inputs are 2 decimals max, so 4 decimals is always enough headroom, and the extra digits
  // strip JS float artefacts (2.07 × 0.95 → 1.9665000000000001 → clean 1.9665).
  const round = (v: number) => Math.round(v * factor * 10000) / 10000;
  const suffixes = ['_NTP', '_TPP', '_TASS', '_BTP', '_TTOT'];

  // Standard sections: IP, CY, BI, GP, LS, OP, HU, KB
  for (const s of ['IP', 'CY', 'BI', 'GP', 'LS', 'OP', 'HU', 'KB']) {
    if (excluded.includes(s)) continue;
    const sec = Array.isArray(pp[s]) ? pp[s][0] : pp[s];
    if (!sec) continue;
    for (const suffix of suffixes) {
      const key = `${s}${suffix}`;
      if (sec[key] !== undefined) sec[key] = round(Number(sec[key]));
    }
  }

  // EC array entries (Mobiele elektronica, Smart Home)
  if (!excluded.includes('EC') && Array.isArray(pp.EC)) {
    for (const ec of pp.EC) {
      for (const suffix of suffixes) {
        const key = `EC${suffix}`;
        if (ec[key] !== undefined) ec[key] = round(Number(ec[key]));
      }
    }
  }

  // Recalculate PP-level totals from all sections (discounted + excluded)
  let ppNtp = 0, ppTpp = 0, ppTass = 0, ppBtp = 0, ppTtot = 0;
  for (const s of ['IP', 'CY', 'BI', 'GP', 'LS', 'OP', 'HU', 'KB']) {
    const sec = Array.isArray(pp[s]) ? pp[s][0] : pp[s];
    if (sec) {
      ppNtp += Number(sec[`${s}_NTP`] || 0);
      ppTpp += Number(sec[`${s}_TPP`] || 0);
      ppTass += Number(sec[`${s}_TASS`] || 0);
      ppBtp += Number(sec[`${s}_BTP`] || 0);
      ppTtot += Number(sec[`${s}_TTOT`] || 0);
    }
  }
  if (Array.isArray(pp.EC)) {
    for (const ec of pp.EC) {
      ppNtp += Number(ec.EC_NTP || 0);
      ppTpp += Number(ec.EC_TPP || 0);
      ppTass += Number(ec.EC_TASS || 0);
      ppBtp += Number(ec.EC_BTP || 0);
      ppTtot += Number(ec.EC_TTOT || 0);
    }
  }
  // PP-level totals stay at 2 decimals for backend / display compatibility. The round also
  // cleans JS float artefacts from summing the 4-decimal section values (1.9665 + 5.6525 +
  // 11.647 can land just below/above the exact sum).
  const round2 = (v: number): number => Math.round(v * 100) / 100;
  pp.PP_NTP = round2(ppNtp);
  pp.PP_TPP = round2(ppTpp);
  pp.PP_TASS = round2(ppTass);
  pp.PP_BTP = round2(ppBtp);
  pp.PP_TTOT = round2(ppTtot);
  pp.PP_NTPVPK = round2(ppNtp);
  // Marker so downstream screens (summary pre-package block, display helpers) know this PP is
  // already discounted and don't re-apply the inboedel discount on top.
  pp._discountBaked = true;
};
export const stripNonAdvisorFields = (data: any) => {
  const pp = data?.Contractdocument?.PP;
  if (!pp) return data;

  // IP: only CODE, GSS, VERZSOM, GARONDV, ERB
  if (pp.IP) {
    const ip = Array.isArray(pp.IP) ? pp.IP[0] : pp.IP;
    if (ip) {
      delete ip.IP_ENTREF;
      ip.IP_PPRC = 20;
      delete ip.IP_VOLGNUM;
      delete ip.IP_TAXVRT;
      delete ip.MP;
    }
  }

  // Coverage sections: set PPRC to 20 (advisor provision), strip ENTREF, VOLGNUM, MP.
  // Only sections present in the data are touched — sections without provision in the
  // advisor format simply never carry a PPRC field here.
  const sections = ['BI', 'EC', 'GP', 'LS', 'HU', 'KB', 'OP'] as const;
  for (const s of sections) {
    const sec = pp[s];
    if (sec && typeof sec === 'object' && !Array.isArray(sec)) {
      sec[`${s}_PPRC`] = 20;
      delete sec[`${s}_ENTREF`];
      delete sec[`${s}_VOLGNUM`];
      delete sec.MP;
    }
  }

  // VR and GA removed - no longer offered
  delete pp.VR;
  delete pp.GA;

  // EC as array: set PPRC to 20 (advisor provision), strip other fields
  if (Array.isArray(pp.EC)) {
    for (const ec of pp.EC) {
      ec.EC_PPRC = 20;
      delete ec.EC_ENTREF;
      delete ec.EC_VOLGNUM;
      delete ec.MP;
    }
  }

  return data;
};

const buildEcRequestRow = (code: string, existing: any, defaultVerzsom: number) => ({
  EC_CODE: code,
  EC_VERZSOM: existing?.EC_VERZSOM || defaultVerzsom,
  EC_ERB: [100, 300, 500].includes(existing?.EC_ERB) ? existing.EC_ERB : 100,
  EC_GEBIED: 'W',
  EC_PPRC: 20,
  EC_WPREMBP: '4',
});

/**
 * The EC array of a request: ONE row (schema maxItems 1), the selected one (Smart Home replaces
 * Mobiele elektronica), or the forced code(s) of a pricing call that prices the row the customer
 * has NOT selected. Empty only when electronics is explicitly off.
 */
export const buildEcRequestRows = (dekkingen: any, existingEc: unknown, forcedCode?: string | string[]): any[] => {
  const codes = forcedCode === undefined
    ? [selectedEcCode(dekkingen)].filter((c): c is string => Boolean(c))
    : Array.isArray(forcedCode) ? forcedCode : [forcedCode];
  const existing = Array.isArray(existingEc) ? existingEc : existingEc ? [existingEc] : [];
  return codes.map((code) => buildEcRequestRow(code, existing.find((e: any) => e?.EC_CODE === code), EC_DEFAULT_SUM[code]));
};

/**
 * Maps form data to calculation request payload
 * Conditionally includes optional coverage sections based on Dekkingen selections.
 * VERZSOM values are only set for enabled coverages; disabled coverages get VERZSOM = 0.
 */
export const mapToCalculationRequest = (formData: any, opts?: { glassDisabled?: boolean; ecCode?: string | string[] }) => {
  // Deep clone to avoid modifying original formData
  const requestData = JSON.parse(JSON.stringify(formData));

  // Ensure nested structure exists
  if (!requestData.Contractdocument) requestData.Contractdocument = {};
  if (!requestData.Contractdocument.PP) requestData.Contractdocument.PP = {};

  // Get coverage selections (from Supportsection or _uiState fallback)
  const dekkingen = requestData.Supportsection?.Dekkingen || requestData._uiState?.Dekkingen || {};
  const isBuitenhuisEnabled = dekkingen.Buitenshuis === 'J';

  // Restore BA_MONUMEN, BA_OPPWON, BA_BOUWJR and BA_HERBWRD from _uiState
  // (cleanInboedelForSchema moves them there). The BA_HERBWRD line is LOAD-BEARING, not
  // cosmetic: the GP gate below asks whether a herbouwwaarde is known and reads the same
  // _uiState fallback, so dropping this line lets GP go out with PP.BA.BA_HERBWRD absent —
  // which the engine answers with XG 11, declining the WHOLE calculation. Every card loses
  // its price, not just glass.
  const uiState = requestData._uiState || formData?._uiState || {};
  if (requestData.Contractdocument.PP.BA) {
    if (requestData.Contractdocument.PP.BA.BA_MONUMEN === undefined && uiState.BA_MONUMEN !== undefined) {
      requestData.Contractdocument.PP.BA.BA_MONUMEN = uiState.BA_MONUMEN;
    }
    if (requestData.Contractdocument.PP.BA.BA_OPPWON === undefined && uiState.BA_OPPWON !== undefined) {
      requestData.Contractdocument.PP.BA.BA_OPPWON = uiState.BA_OPPWON;
    }
    if (requestData.Contractdocument.PP.BA.BA_BOUWJR === undefined && uiState.BA_BOUWJR !== undefined) {
      requestData.Contractdocument.PP.BA.BA_BOUWJR = uiState.BA_BOUWJR;
    }
    if (requestData.Contractdocument.PP.BA.BA_HERBWRD === undefined && uiState.BA_HERBWRD !== undefined) {
      requestData.Contractdocument.PP.BA.BA_HERBWRD = Number(uiState.BA_HERBWRD);
    }
  }

  // Always All Risk (5016)
  const ipCode = '5016';
  // Ensure IP is an object (schema save converts to array, BFF expects object)
  if (Array.isArray(requestData.Contractdocument.PP.IP)) {
    requestData.Contractdocument.PP.IP = requestData.Contractdocument.PP.IP[0] || {};
  }
  if (!requestData.Contractdocument.PP.IP) requestData.Contractdocument.PP.IP = {};
  requestData.Contractdocument.PP.IP.IP_CODE = ipCode;
  // Advisor format: IP only has CODE, GSS, VERZSOM, GARONDV, ERB - no ENTREF/VOLGNUM/TAXVRT/MP
  delete requestData.Contractdocument.PP.IP.IP_ENTREF;
  requestData.Contractdocument.PP.IP.IP_PPRC = 20;
  if (!requestData.Contractdocument.PP.IP.IP_WPREMBP) requestData.Contractdocument.PP.IP.IP_WPREMBP = '4';
  if (!requestData.Contractdocument.PP.IP.IP_GEBIED) requestData.Contractdocument.PP.IP.IP_GEBIED = 'W';
  // IP_ERB (Eigen risico / own risk): preserve a valid choice (100/300/500),
  // otherwise default to 100.
  if (![100, 300, 500].includes(requestData.Contractdocument.PP.IP.IP_ERB)) {
    requestData.Contractdocument.PP.IP.IP_ERB = 100;
  }
  delete requestData.Contractdocument.PP.IP.IP_VOLGNUM;
  delete requestData.Contractdocument.PP.IP.IP_TAXVRT;
  delete requestData.Contractdocument.PP.IP.MP;

  // Ensure required PP fields are set (maatschappij identification)
  if (!requestData.Contractdocument.PP.PP_MYAAND) {
    requestData.Contractdocument.PP.PP_MYAAND = CARRIER_CODE;
  }
  if (!requestData.Contractdocument.PP.PP_PRODNM) {
    requestData.Contractdocument.PP.PP_PRODNM = PRODUCT_NAME;
  }
  if (!requestData.Contractdocument.PP.PP_PRODUCT) {
    requestData.Contractdocument.PP.PP_PRODUCT = requestData.Contractdocument.PP.PP_PRODNM;
  }
  requestData.Contractdocument.PP.PP_STPRV = 'J';

  // BI (Buitenhuis) section - advisor format: CODE, VERZSOM, ERB only
  if (!requestData.Contractdocument.PP.BI) {
    requestData.Contractdocument.PP.BI = {};
  }
  if (!requestData.Contractdocument.PP.BI.BI_CODE) {
    requestData.Contractdocument.PP.BI.BI_CODE = '5045';
  }
  if (requestData.Contractdocument.PP.BI.BI_VERZSOM === undefined) {
    requestData.Contractdocument.PP.BI.BI_VERZSOM = isBuitenhuisEnabled ? 7500 : 0;
  } else if (!isBuitenhuisEnabled) {
    requestData.Contractdocument.PP.BI.BI_VERZSOM = 0;
  }
  if ([100, 300, 500].includes(requestData.Contractdocument.PP.IP.IP_ERB)) {
    requestData.Contractdocument.PP.BI.BI_ERB = requestData.Contractdocument.PP.IP.IP_ERB;
  } else if (![100, 300, 500].includes(requestData.Contractdocument.PP.BI.BI_ERB)) {
    requestData.Contractdocument.PP.BI.BI_ERB = 100;
  }
  // Strip fields not in advisor format
  requestData.Contractdocument.PP.BI.BI_PPRC = 20;
  if (!requestData.Contractdocument.PP.BI.BI_WPREMBP) requestData.Contractdocument.PP.BI.BI_WPREMBP = '4';
  delete requestData.Contractdocument.PP.BI.BI_ENTREF;
  delete requestData.Contractdocument.PP.BI.BI_VOLGNUM;
  delete requestData.Contractdocument.PP.BI.MP;

  // GP (Glas) — rated by the Midglas engine over BA_HERBWRD. Sent ONLY when a
  // rebuild value is known: without it the engine declines the whole request,
  // which would leave every coverage card unpriced instead of just glass.
  // readInboedelGlassAmount is the ONE reader of that value (validation and the
  // calc signature use it too), so the three can never disagree about whether a
  // herbouwwaarde exists.
  if (!opts?.glassDisabled && readInboedelGlassAmount(requestData) > 0) {
    requestData.Contractdocument.PP.GP = buildGlassRequestBlock();
  } else {
    delete requestData.Contractdocument.PP.GP;
  }

  // OP (Eigenaarsbelang) - always include to get premium values for display
  if (!requestData.Contractdocument.PP.OP) requestData.Contractdocument.PP.OP = {};
  if (!requestData.Contractdocument.PP.OP.OP_CODE) requestData.Contractdocument.PP.OP.OP_CODE = '5010';
  if (!requestData.Contractdocument.PP.OP.OP_VERZSOM) requestData.Contractdocument.PP.OP.OP_VERZSOM = 20000;
  requestData.Contractdocument.PP.OP.OP_PPRC = 20;
  if (!requestData.Contractdocument.PP.OP.OP_WPREMBP) requestData.Contractdocument.PP.OP.OP_WPREMBP = '4';
  delete requestData.Contractdocument.PP.OP.OP_ENTREF;
  delete requestData.Contractdocument.PP.OP.OP_VOLGNUM;

  // EC: ONE row (schema maxItems 1) — the selected one, or the code the caller forces to price
  // the other row for its badge. Built fresh to avoid mutation corruption from ensureEc.
  requestData.Contractdocument.PP.EC = buildEcRequestRows(dekkingen, requestData.Contractdocument.PP.EC, opts?.ecCode);

  // LS (Lijfsieraden) section - advisor format: CODE, VERZSOM only
  if (!requestData.Contractdocument.PP.LS) {
    requestData.Contractdocument.PP.LS = {};
  }
  if (!requestData.Contractdocument.PP.LS.LS_CODE) {
    requestData.Contractdocument.PP.LS.LS_CODE = '5950';
  }
  if (requestData.Contractdocument.PP.LS.LS_VERZSOM === undefined) {
    requestData.Contractdocument.PP.LS.LS_VERZSOM = 20000;
  }
  // Strip fields not in advisor format
  requestData.Contractdocument.PP.LS.LS_PPRC = 20;
  if (!requestData.Contractdocument.PP.LS.LS_WPREMBP) requestData.Contractdocument.PP.LS.LS_WPREMBP = '4';
  delete requestData.Contractdocument.PP.LS.LS_ENTREF;
  delete requestData.Contractdocument.PP.LS.LS_VOLGNUM;

  // VR (Verhuur) - no longer offered, always delete
  delete requestData.Contractdocument.PP.VR;

  // HU (Eigenaar-huurdersbelang) section - advisor format: CODE, VERZSOM only
  if (!requestData.Contractdocument.PP.HU) {
    requestData.Contractdocument.PP.HU = {};
  }
  if (!requestData.Contractdocument.PP.HU.HU_CODE) {
    requestData.Contractdocument.PP.HU.HU_CODE = '5970';
  }
  if (requestData.Contractdocument.PP.HU.HU_VERZSOM === undefined) {
    requestData.Contractdocument.PP.HU.HU_VERZSOM = 20000;
  }
  // Strip fields not in advisor format
  requestData.Contractdocument.PP.HU.HU_PPRC = 20;
  if (!requestData.Contractdocument.PP.HU.HU_WPREMBP) requestData.Contractdocument.PP.HU.HU_WPREMBP = '4';
  delete requestData.Contractdocument.PP.HU.HU_ENTREF;
  delete requestData.Contractdocument.PP.HU.HU_VOLGNUM;

  // KB (Bijzondere bezittingen) section - advisor format: CODE, VERZSOM only
  if (!requestData.Contractdocument.PP.KB) {
    requestData.Contractdocument.PP.KB = {};
  }
  if (!requestData.Contractdocument.PP.KB.KB_CODE) {
    requestData.Contractdocument.PP.KB.KB_CODE = '5302';
  }
  if (requestData.Contractdocument.PP.KB.KB_VERZSOM === undefined) {
    requestData.Contractdocument.PP.KB.KB_VERZSOM = 20000;
  }
  // Strip fields not in advisor format
  requestData.Contractdocument.PP.KB.KB_PPRC = 20;
  if (!requestData.Contractdocument.PP.KB.KB_WPREMBP) requestData.Contractdocument.PP.KB.KB_WPREMBP = '4';
  delete requestData.Contractdocument.PP.KB.KB_ENTREF;
  delete requestData.Contractdocument.PP.KB.KB_VOLGNUM;

  // Apply full schema compliance cleanup then strip to advisor format
  const cleanedData = cleanInboedelForSchema(requestData);
  delete cleanedData._uiState;
  delete cleanedData.Supportsection;
  stripNonAdvisorFields(cleanedData);

  // Strip sections not supported
  if (cleanedData?.Contractdocument?.PP) {
    delete cleanedData.Contractdocument.PP.CY;
    // In this mapper OP and GP are ALWAYS included, so both are priced upfront for display.
  }

  return {
    carrierCode: CARRIER_CODE,
    productName: PRODUCT_NAME,
    input: cleanedData
  };
};

/**
 * Maps form data to calculation request with ONLY selected coverages.
 * Used by the "data calculation" in handleNext to get correct CL/VW records.
 */
export const mapToSelectedCoveragesRequest = (formData: any, opts?: { glassDisabled?: boolean }) => {
  const requestData = JSON.parse(JSON.stringify(formData));

  if (!requestData.Contractdocument) requestData.Contractdocument = {};
  if (!requestData.Contractdocument.PP) requestData.Contractdocument.PP = {};

  // Ensure required PP fields
  if (!requestData.Contractdocument.PP.PP_MYAAND) {
    requestData.Contractdocument.PP.PP_MYAAND = CARRIER_CODE;
  }
  if (!requestData.Contractdocument.PP.PP_PRODNM) {
    requestData.Contractdocument.PP.PP_PRODNM = PRODUCT_NAME;
  }
  if (!requestData.Contractdocument.PP.PP_PRODUCT) {
    requestData.Contractdocument.PP.PP_PRODUCT = requestData.Contractdocument.PP.PP_PRODNM;
  }
  requestData.Contractdocument.PP.PP_STPRV = 'J';

  // Ensure IP is an object (schema save converts to array, BFF expects object)
  if (Array.isArray(requestData.Contractdocument.PP.IP)) {
    requestData.Contractdocument.PP.IP = requestData.Contractdocument.PP.IP[0] || {};
  }
  if (!requestData.Contractdocument.PP.IP) requestData.Contractdocument.PP.IP = {};
  // Always All Risk (5016) — mirrors mapToCalculationRequest
  requestData.Contractdocument.PP.IP.IP_CODE = '5016';
  if (!requestData.Contractdocument.PP.IP.IP_WPREMBP) requestData.Contractdocument.PP.IP.IP_WPREMBP = '4';
  if (!requestData.Contractdocument.PP.IP.IP_GEBIED) requestData.Contractdocument.PP.IP.IP_GEBIED = 'W';
  // IP_ERB (Eigen risico / own risk): preserve a valid choice (100/300/500),
  // otherwise default to 100.
  if (![100, 300, 500].includes(requestData.Contractdocument.PP.IP.IP_ERB)) {
    requestData.Contractdocument.PP.IP.IP_ERB = 100;
  }

  // Restore BA_MONUMEN, BA_OPPWON, BA_BOUWJR and BA_HERBWRD from _uiState
  // (cleanInboedelForSchema moves them there). The BA_HERBWRD line is LOAD-BEARING, not
  // cosmetic: the GP gate below asks whether a herbouwwaarde is known and reads the same
  // _uiState fallback, so dropping this line lets GP go out with PP.BA.BA_HERBWRD absent —
  // which the engine answers with XG 11, declining the WHOLE calculation. Every card loses
  // its price, not just glass.
  const uiStateSelected = requestData._uiState || formData?._uiState || {};
  if (requestData.Contractdocument.PP.BA) {
    if (requestData.Contractdocument.PP.BA.BA_MONUMEN === undefined && uiStateSelected.BA_MONUMEN !== undefined) {
      requestData.Contractdocument.PP.BA.BA_MONUMEN = uiStateSelected.BA_MONUMEN;
    }
    if (requestData.Contractdocument.PP.BA.BA_OPPWON === undefined && uiStateSelected.BA_OPPWON !== undefined) {
      requestData.Contractdocument.PP.BA.BA_OPPWON = uiStateSelected.BA_OPPWON;
    }
    if (requestData.Contractdocument.PP.BA.BA_BOUWJR === undefined && uiStateSelected.BA_BOUWJR !== undefined) {
      requestData.Contractdocument.PP.BA.BA_BOUWJR = uiStateSelected.BA_BOUWJR;
    }
    if (requestData.Contractdocument.PP.BA.BA_HERBWRD === undefined && uiStateSelected.BA_HERBWRD !== undefined) {
      requestData.Contractdocument.PP.BA.BA_HERBWRD = Number(uiStateSelected.BA_HERBWRD);
    }
  }

  // Same legacy-storage fallback as mapToCalculationRequest: selections saved before
  // Supportsection preservation live only in _uiState — without the fallback every
  // optional coverage would silently count as deselected.
  const dekkingen = formData?.Supportsection?.Dekkingen || formData?._uiState?.Dekkingen || {};

  // CY (Cyber) - not in SAA schema
  delete requestData.Contractdocument.PP.CY;

  // GP (Glas) - only when selected AND a rebuild value is known; the Midglas
  // engine rates it over BA_HERBWRD and declines the whole request without one.
  if (!opts?.glassDisabled && dekkingen.Glas === 'J' && readInboedelGlassAmount(requestData) > 0) {
    requestData.Contractdocument.PP.GP = buildGlassRequestBlock();
  } else {
    delete requestData.Contractdocument.PP.GP;
  }

  // OP (Eigenaarsbelang) - include for calculation when selected
  if (dekkingen.Eigenaarsbelang === 'J') {
    if (!requestData.Contractdocument.PP.OP) requestData.Contractdocument.PP.OP = {};
    if (!requestData.Contractdocument.PP.OP.OP_CODE) requestData.Contractdocument.PP.OP.OP_CODE = '5010';
    if (!requestData.Contractdocument.PP.OP.OP_VERZSOM) requestData.Contractdocument.PP.OP.OP_VERZSOM = 20000;
    requestData.Contractdocument.PP.OP.OP_PPRC = 20;
    if (!requestData.Contractdocument.PP.OP.OP_WPREMBP) requestData.Contractdocument.PP.OP.OP_WPREMBP = '4';
    delete requestData.Contractdocument.PP.OP.OP_ENTREF;
    delete requestData.Contractdocument.PP.OP.OP_VOLGNUM;
  } else {
    delete requestData.Contractdocument.PP.OP;
  }

  // BI (Buitenhuis) - only include if selected, advisor format
  if (dekkingen.Buitenshuis === 'J') {
    if (!requestData.Contractdocument.PP.BI) requestData.Contractdocument.PP.BI = {};
    if (!requestData.Contractdocument.PP.BI.BI_CODE) requestData.Contractdocument.PP.BI.BI_CODE = '5045';
    if (requestData.Contractdocument.PP.BI.BI_VERZSOM === undefined) requestData.Contractdocument.PP.BI.BI_VERZSOM = 7500;
    if ([100, 300, 500].includes(requestData.Contractdocument.PP.IP?.IP_ERB)) {
      requestData.Contractdocument.PP.BI.BI_ERB = requestData.Contractdocument.PP.IP.IP_ERB;
    } else if (![100, 300, 500].includes(requestData.Contractdocument.PP.BI.BI_ERB)) {
      requestData.Contractdocument.PP.BI.BI_ERB = 100;
    }
    requestData.Contractdocument.PP.BI.BI_PPRC = 20;
    if (!requestData.Contractdocument.PP.BI.BI_WPREMBP) requestData.Contractdocument.PP.BI.BI_WPREMBP = '4';
    delete requestData.Contractdocument.PP.BI.BI_ENTREF;
    delete requestData.Contractdocument.PP.BI.BI_VOLGNUM;
    delete requestData.Contractdocument.PP.BI.MP;
  } else {
    delete requestData.Contractdocument.PP.BI;
  }

  // EC: ONE row (schema maxItems 1) — Smart Home replaces Mobiele elektronica when selected.
  {
    const ecEntries = buildEcRequestRows(dekkingen, requestData.Contractdocument.PP.EC);
    if (ecEntries.length > 0) {
      requestData.Contractdocument.PP.EC = ecEntries;
    } else {
      delete requestData.Contractdocument.PP.EC;
    }
  }

  // LS (Lijfsieraden) - only include if selected, advisor format
  if (dekkingen.Lijfsieraden === 'J') {
    if (!requestData.Contractdocument.PP.LS) requestData.Contractdocument.PP.LS = {};
    if (!requestData.Contractdocument.PP.LS.LS_CODE) requestData.Contractdocument.PP.LS.LS_CODE = '5950';
    if (requestData.Contractdocument.PP.LS.LS_VERZSOM === undefined) requestData.Contractdocument.PP.LS.LS_VERZSOM = 20000;
    requestData.Contractdocument.PP.LS.LS_PPRC = 20;
    if (!requestData.Contractdocument.PP.LS.LS_WPREMBP) requestData.Contractdocument.PP.LS.LS_WPREMBP = '4';
    delete requestData.Contractdocument.PP.LS.LS_ENTREF;
    delete requestData.Contractdocument.PP.LS.LS_VOLGNUM;
  } else {
    delete requestData.Contractdocument.PP.LS;
  }

  // VR (Verhuur) - no longer offered
  delete requestData.Contractdocument.PP.VR;

  // HU (Eigenaar-huurdersbelang) - only include if selected, advisor format
  if (dekkingen.Huurdersbelang === 'J') {
    if (!requestData.Contractdocument.PP.HU) requestData.Contractdocument.PP.HU = {};
    if (!requestData.Contractdocument.PP.HU.HU_CODE) requestData.Contractdocument.PP.HU.HU_CODE = '5970';
    if (requestData.Contractdocument.PP.HU.HU_VERZSOM === undefined) requestData.Contractdocument.PP.HU.HU_VERZSOM = 20000;
    requestData.Contractdocument.PP.HU.HU_PPRC = 20;
    if (!requestData.Contractdocument.PP.HU.HU_WPREMBP) requestData.Contractdocument.PP.HU.HU_WPREMBP = '4';
    delete requestData.Contractdocument.PP.HU.HU_ENTREF;
    delete requestData.Contractdocument.PP.HU.HU_VOLGNUM;
  } else {
    delete requestData.Contractdocument.PP.HU;
  }

  // KB (Bijzondere bezittingen) - only include if selected, advisor format
  if (dekkingen.Bijzonderebezittingen === 'J') {
    if (!requestData.Contractdocument.PP.KB) requestData.Contractdocument.PP.KB = {};
    if (!requestData.Contractdocument.PP.KB.KB_CODE) requestData.Contractdocument.PP.KB.KB_CODE = '5302';
    if (requestData.Contractdocument.PP.KB.KB_VERZSOM === undefined) requestData.Contractdocument.PP.KB.KB_VERZSOM = 20000;
    requestData.Contractdocument.PP.KB.KB_PPRC = 20;
    if (!requestData.Contractdocument.PP.KB.KB_WPREMBP) requestData.Contractdocument.PP.KB.KB_WPREMBP = '4';
    delete requestData.Contractdocument.PP.KB.KB_ENTREF;
    delete requestData.Contractdocument.PP.KB.KB_VOLGNUM;
  } else {
    delete requestData.Contractdocument.PP.KB;
  }

  // GA (Onbeperkte nieuwwaarde) - no longer offered
  delete requestData.Contractdocument.PP.GA;

  // Apply schema compliance cleanup then strip to advisor format
  const cleanedData = cleanInboedelForSchema(requestData);
  delete cleanedData._uiState;
  delete cleanedData.Supportsection;
  stripNonAdvisorFields(cleanedData);

  return {
    carrierCode: CARRIER_CODE,
    productName: PRODUCT_NAME,
    input: cleanedData
  };
};

/**
 * Maps calculation response to form data updates
 * Clears premiums for unselected coverages.
 */
export const mapFromCalculationResponse = (response: any, currentFormData: any) => {
  if (!response || !response.output) {
    return currentFormData;
  }

  // Preserve user-input fields that the BFF may not return
  const prevBA = currentFormData?.Contractdocument?.PP?.BA;
  const prevIB = currentFormData?.Contractdocument?.PP?.IB;
  const prevVP = currentFormData?.Contractdocument?.PP?.VP;
  const prevIP = Array.isArray(currentFormData?.Contractdocument?.PP?.IP)
    ? currentFormData.Contractdocument.PP.IP[0]
    : currentFormData?.Contractdocument?.PP?.IP;
  const prevSupportsection = currentFormData?.Supportsection;

  // Merge response with current form data
  const updatedData = {
    ...currentFormData,
    ...response.output
  };

  // Restore UI-only fields lost in the shallow merge
  if (updatedData?.Contractdocument?.PP) {
    // A response without a BA section would otherwise skip every BA restore below and wipe
    // the user's building fields (incl. BA_HERBWRD, which drives the Midglas GP injection).
    if (prevBA && !updatedData.Contractdocument.PP.BA) {
      updatedData.Contractdocument.PP.BA = {};
    }
    // BA_OPPWON - sent to the BFF but not echoed back in the calculate response;
    // restore from previous form data
    if (prevBA?.BA_OPPWON !== undefined && updatedData.Contractdocument.PP.BA) {
      if (updatedData.Contractdocument.PP.BA.BA_OPPWON === undefined) {
        updatedData.Contractdocument.PP.BA.BA_OPPWON = prevBA.BA_OPPWON;
      }
    }
    // BA_MONUMEN - sent to the BFF but not echoed back in the calculate response;
    // restore from previous form data
    if (prevBA?.BA_MONUMEN !== undefined && updatedData.Contractdocument.PP.BA) {
      if (updatedData.Contractdocument.PP.BA.BA_MONUMEN === undefined) {
        updatedData.Contractdocument.PP.BA.BA_MONUMEN = prevBA.BA_MONUMEN;
      }
    }
    // User/Infofolio-supplied BA fields the BFF calculate response does not echo back —
    // restore so a successful calculation doesn't wipe them from the form.
    if (updatedData.Contractdocument.PP.BA) {
      for (const k of ['BA_BOUWJR', 'BA_HERBWRD', 'BA_HERBVST', 'BA_TYPEWON', 'BA_WONING'] as const) {
        if (prevBA?.[k] !== undefined && updatedData.Contractdocument.PP.BA[k] === undefined) {
          updatedData.Contractdocument.PP.BA[k] = prevBA[k];
        }
      }
    }
    // IB (Vaststelling inboedelwaarde) - not in schema, BFF never returns it
    if (prevIB?.IB_INBVST && !updatedData.Contractdocument.PP.IB?.IB_INBVST) {
      if (!updatedData.Contractdocument.PP.IB) updatedData.Contractdocument.PP.IB = {};
      updatedData.Contractdocument.PP.IB.IB_INBVST = prevIB.IB_INBVST;
    }

    if (prevVP) {
      for (const f of VP_ADDRESS_UI_FIELDS) {
        if (prevVP[f] !== undefined && updatedData.Contractdocument.PP.VP?.[f] === undefined) {
          if (!updatedData.Contractdocument.PP.VP) updatedData.Contractdocument.PP.VP = {};
          updatedData.Contractdocument.PP.VP[f] = prevVP[f];
        }
      }
    }
  }

  // Restore Supportsection (Dekkingen, Divers) - not in BFF response
  if (prevSupportsection && !updatedData.Supportsection) {
    updatedData.Supportsection = prevSupportsection;
  } else if (prevSupportsection && updatedData.Supportsection) {
    updatedData.Supportsection = { ...prevSupportsection, ...updatedData.Supportsection };
  }

  // Ensure IP is object for UI compatibility (BFF may return array)
  if (Array.isArray(updatedData?.Contractdocument?.PP?.IP)) {
    updatedData.Contractdocument.PP.IP = updatedData.Contractdocument.PP.IP[0] || {};
  }
  // EC stays as array — both Mobiele elektronica (5450) and Smart Home (5470) live here
  if (updatedData?.Contractdocument?.PP?.EC && !Array.isArray(updatedData.Contractdocument.PP.EC)) {
    updatedData.Contractdocument.PP.EC = [updatedData.Contractdocument.PP.EC];
  }

  // IP_ERB (Eigen risico) - BFF response may drop it or return as string.
  // Preserve user's selection and ensure it's a number for validation.
  if (updatedData?.Contractdocument?.PP?.IP) {
    const prevErb = prevIP?.IP_ERB;
    const responseErb = updatedData.Contractdocument.PP.IP.IP_ERB;
    if (responseErb !== undefined) {
      // BFF returned it — ensure numeric type (BFF may return string "300")
      updatedData.Contractdocument.PP.IP.IP_ERB = Number(responseErb);
    } else if (prevErb !== undefined) {
      // BFF dropped it — restore from previous form data
      updatedData.Contractdocument.PP.IP.IP_ERB = Number(prevErb);
    }

    // IP_GSS (Gezinssamenstelling) - BFF response may not return it
    if (!updatedData.Contractdocument.PP.IP.IP_GSS && prevIP?.IP_GSS) {
      updatedData.Contractdocument.PP.IP.IP_GSS = prevIP.IP_GSS;
    }

    // IP_PPRC - BFF returns as string, must be number
    if (updatedData.Contractdocument.PP.IP.IP_PPRC !== undefined) {
      updatedData.Contractdocument.PP.IP.IP_PPRC = Number(updatedData.Contractdocument.PP.IP.IP_PPRC);
    }
  }

  // EC: BFF returns PPRC as string, and doesn't return ENTREF/VOLGNUM — fix all
  if (Array.isArray(updatedData?.Contractdocument?.PP?.EC)) {
    for (const ec of updatedData.Contractdocument.PP.EC) {
      if (ec.EC_PPRC !== undefined) ec.EC_PPRC = Number(ec.EC_PPRC);
      if (!ec.EC_ENTREF && ec.EC_CODE) ec.EC_ENTREF = ec.EC_CODE;
      if (ec.EC_VOLGNUM === undefined) ec.EC_VOLGNUM = 1;
    }
  }

  // PP_NTPVPK and PP_PPRC — BFF doesn't return these, ensure they're set
  if (updatedData?.Contractdocument?.PP) {
    const pp = updatedData.Contractdocument.PP;
    if (pp.PP_NTPVPK === undefined && pp.PP_NTP !== undefined) {
      pp.PP_NTPVPK = Number(pp.PP_NTP);
    }
    if (pp.PP_PPRC === undefined) {
      pp.PP_PPRC = 20;
    }
  }

  return updatedData;
};

/**
 * Format currency for display - locale-aware
 * DIVERGES from woonhuis/calc.ts formatCurrency (digit-strip + maximumFractionDigits: 0):
 * this one parseFloat+Math.round's, so "1234.56" formats differently. Which behaviour should
 * win is an open design decision — do not unify without deciding.
 */
export const formatCurrency = (value: number | string): string => {
  if (!value && value !== 0) return '';
  // Parse as float first to handle string values like "89000.00"
  const parsed = typeof value === 'number' ? value : parseFloat(String(value));
  if (isNaN(parsed)) return '';
  const num = Math.round(parsed);
  return num.toLocaleString(getCurrentLocale());
};

export { unformatCurrency } from '../../../utils/currency';

/**
 * Format postcode for display (e.g., "3905NK" -> "3905 NK")
 */
export const formatPostcode = (value: string): string => {
  if (!value) return '';
  const cleaned = value.replace(/\s/g, '').toUpperCase();
  if (cleaned.length > 4) {
    return `${cleaned.slice(0, 4)} ${cleaned.slice(4)}`;
  }
  return cleaned;
};

/**
 * Remove formatting from postcode (e.g., "3905 NK" -> "3905NK")
 */
export const unformatPostcode = (value: string): string => {
  return value.replace(/\s/g, '').toUpperCase();
};

/**
 * Recalculates premium totals based on selected coverages.
 * IMPORTANT: Coverage sections are PRESERVED (not deleted) to keep prices available
 * when the user navigates back to this page. Only PP_TTOT is recalculated.
 *
 * Base product (Inboedel - IP) is always included as it's selected on product page.
 * The PP_TTOT reflects only selected coverages for display on summary page.
 *
 * Uses IP_TTOT as base (like Woonhuis uses OP_TTOT). IP_TTOT is NOT updated to prevent stacking.
 */
export const cleanUnselectedCoveragePremiums = (data: any) => {
  const cleanedData = cleanInboedelForSchema(data);
  const pp = cleanedData?.Contractdocument?.PP;

  const dekkingen = cleanedData?.Supportsection?.Dekkingen || cleanedData?._uiState?.Dekkingen || {};
  const isGlasSelected = dekkingen.Glas === 'J';
  const isLijfsieradenSelected = dekkingen.Lijfsieraden === 'J';
  const isEigenaarsbelangSelected = dekkingen.Eigenaarsbelang === 'J';
  const isHuurdersbelangSelected = dekkingen.Huurdersbelang === 'J';
  const isBijzondereBezittingenSelected = dekkingen.Bijzonderebezittingen === 'J';
  const isSmarthomeSelected = dekkingen.Smarthome === 'J';

  // CRITICAL: Do NOT update IP_TTOT - only PP_TTOT gets updated to prevent stacking
  // IMPORTANT: Use Number() coercion - BFF returns TTOT as strings
  const ip = Array.isArray(pp?.IP) ? pp.IP[0] : pp?.IP;
  const ipPremium = Number(ip?.IP_TTOT) || 0;
  const buitenhuisPremium = Number(pp?.BI?.BI_TTOT) || 0;
  const mobileElectronicsPremium = Number(getEc(pp, EC_CODE_MOBIEL)?.EC_TTOT) || 0;

  const basePremium = ipPremium + buitenhuisPremium + mobileElectronicsPremium;

  // Optional coverage premiums
  const glassPremium = Number(pp?.GP?.GP_TTOT) || 0;
  const smarthomePremium = Number(getEc(pp, EC_CODE_SMARTHOME)?.EC_TTOT) || 0;
  const jewelryPremium = Number(pp?.LS?.LS_TTOT) || 0;
  const eigenaarsbelangPremium = Number(pp?.OP?.OP_TTOT) || 0;
  const huurdersbelangPremium = Number(pp?.HU?.HU_TTOT) || 0;
  const specialPossessionsPremium = Number(pp?.KB?.KB_TTOT) || 0;

  // Recalculate totals: base + only selected optional coverages
  const selectedCoveragesTotal =
    (isGlasSelected ? glassPremium : 0) +
    (isLijfsieradenSelected ? jewelryPremium : 0) +
    (isEigenaarsbelangSelected ? eigenaarsbelangPremium : 0) +
    (isHuurdersbelangSelected ? huurdersbelangPremium : 0) +
    (isBijzondereBezittingenSelected ? specialPossessionsPremium : 0) +
    (isSmarthomeSelected ? smarthomePremium : 0);

  // Round to 2 decimals to avoid JS float artefacts from chained additions of per-section TTOTs.
  const newTotal = Math.round((basePremium + selectedCoveragesTotal) * 100) / 100;

  // Only update PP_TTOT (product total) - this is what the summary page uses
  // CRITICAL: Do NOT update IP_TTOT to prevent stacking on subsequent navigations
  if (pp) {
    pp.PP_TTOT = newTotal;
  }

  return cleanedData;
};

/**
 * Migrate legacy Dekkingen key names to the schema-compliant names and DELETE the legacy
 * keys (incl. the retired Inboedel_allrisk). Single source for the migration — used by both
 * applyProductDefaults and cleanInboedelForSchema. Mutates `dek` in place.
 */
export const migrateLegacyDekkingenKeys = (dek: any): void => {
  if (!dek || typeof dek !== 'object') return;
  if (dek.Buitenhuis !== undefined) {
    if (dek.Buitenshuis === undefined) dek.Buitenshuis = dek.Buitenhuis;
    delete dek.Buitenhuis;
  }
  if (dek.Mobiele_elektronica !== undefined) {
    if (dek.Mobieleelektronica === undefined) dek.Mobieleelektronica = dek.Mobiele_elektronica;
    delete dek.Mobiele_elektronica;
  }
  if (dek.Eigenaar_huurdersbelang !== undefined) {
    if (dek.Huurdersbelang === undefined) dek.Huurdersbelang = dek.Eigenaar_huurdersbelang;
    delete dek.Eigenaar_huurdersbelang;
  }
  if (dek.Bijzondere_bezittingen !== undefined) {
    if (dek.Bijzonderebezittingen === undefined) dek.Bijzonderebezittingen = dek.Bijzondere_bezittingen;
    delete dek.Bijzondere_bezittingen;
  }
  if (dek.Inboedel_allrisk !== undefined) {
    delete dek.Inboedel_allrisk;
  }
};

/**
 * Comprehensive schema compliance cleanup for Inboedel step data.
 * Uses WHITELIST approach: only properties defined in inboedel jsonschema.json are kept.
 * UI-only fields (IB, CY, PP_PPRC) are preserved at root-level _uiState (root has no
 * additionalProperties restriction in schema); BA_OPPWON/BA_MONUMEN/BA_BOUWJR are stashed
 * there too as a backup even though they stay in PP.BA (they are in the BA whitelist).
 * IP/EC are converted from objects to arrays (schema requires arrays).
 * Relatiedocument is DELETED — relation data is consumed by the relation call elsewhere,
 * not by this product's BFF payload.
 */
export const cleanInboedelForSchema = (data: any): any => {
  const clean = JSON.parse(JSON.stringify(data));

  // Preserve UI-only state at root level (root has no additionalProperties restriction)
  if (!clean._uiState) clean._uiState = {};

  const pp = clean?.Contractdocument?.PP;
  if (pp) {
    // Save UI-only PP fields before stripping
    if (pp.IB?.IB_INBVST) clean._uiState.IB_INBVST = pp.IB.IB_INBVST;
    if (pp.CY) clean._uiState.CY = { ...pp.CY };
    if (pp.PP_PPRC !== undefined) clean._uiState.PP_PPRC = pp.PP_PPRC;

    // VP address — the whitelist below reduces PP.VP to VP_GEBDAT/VP_PARZAKC for the wire;
    // stash the address so the Adresgegevens inputs can re-seed after a round-trip.
    if (pp.VP) {
      for (const f of VP_ADDRESS_UI_FIELDS) {
        if (pp.VP[f] !== undefined && pp.VP[f] !== '') clean._uiState[f] = pp.VP[f];
      }
    }

    // Stash BA fields at root._uiState as a backup (other screens read the stash, and the
    // data-load effect restores on next mount). All of them are IN the BA whitelist below and
    // therefore sent to the BFF; the stash is a harmless backup, and for BA_HERBWRD it also
    // keeps step data saved before the field was accepted on the Inboedel wire readable.
    if (pp.BA) {
      if (pp.BA.BA_OPPWON !== undefined) clean._uiState.BA_OPPWON = pp.BA.BA_OPPWON;
      if (pp.BA.BA_MONUMEN !== undefined) clean._uiState.BA_MONUMEN = pp.BA.BA_MONUMEN;
      if (pp.BA.BA_BOUWJR !== undefined) clean._uiState.BA_BOUWJR = pp.BA.BA_BOUWJR;
      if (pp.BA.BA_HERBWRD !== undefined) clean._uiState.BA_HERBWRD = pp.BA.BA_HERBWRD;
    }

    // === PP property whitelist (from inboedel jsonschema.json, additionalProperties: false) ===
    const allowedPP = new Set([
      'BA', 'BI', 'CL', 'EC', 'GA', 'GP', 'HU', 'IP', 'KB', 'LS', 'OP', 'RI',
      'TP', 'VE', 'VL', 'VP', 'VR', 'VW', 'QS',
      'PP_ADEFVRS', 'PP_AFDDEFN', 'PP_AFWAFST', 'PP_AGNTNR',
      'PP_BETTERM', 'PP_BRANCHE', 'PP_COLL', 'PP_CDUUMND', 'PP_CVS', 'PP_EKST',
      'PP_EXPDAT', 'PP_EXTERN', 'PP_HANDACC', 'PP_HVVDAT', 'PP_INCWIJZ', 'PP_INGDAT', 'PP_INGDTW',
      'PP_MYAAND', 'PP_MYNAAM', 'PP_NTP', 'PP_PKRTVMP', 'PP_PRODNM', 'PP_PRODUCT', 'PP_PTSLTB',
      'PP_PVADAT', 'PP_RDNMUT', 'PP_SPECLIM', 'PP_SPLIMNR', 'PP_STATUS', 'PP_STPRV',
      'PP_TARDAT', 'PP_TASS', 'PP_TPP', 'PP_TTOT', 'PP_TTPPRV',
      'PP_TTSLTB', 'PP_VRWRKCD', 'PP_WPREMBP',
    ]);
    for (const key of Object.keys(pp)) {
      if (!allowedPP.has(key)) delete pp[key];
    }

    // === PP.VP whitelist: only VP_GEBDAT and VP_PARZAKC (additionalProperties: false) ===
    if (pp.VP) {
      const { VP_GEBDAT, VP_PARZAKC } = pp.VP;
      pp.VP = {
        VP_PARZAKC: VP_PARZAKC || 'P',
        ...(VP_GEBDAT !== undefined ? { VP_GEBDAT: Number(VP_GEBDAT) } : {}),
      };
    }

    // OP_VERZSOM stays at default (25000) - OP is Opruimingskosten, not herbouwwaarde

    // === PP.BA whitelist (additionalProperties: false) ===
    if (pp.BA) {
      for (const key of Object.keys(pp.BA)) {
        if (!INBOEDEL_ALLOWED_BA_FIELDS.has(key)) delete pp.BA[key];
      }
      // BA_HERBWRD must be a number per schema
      if (pp.BA.BA_HERBWRD !== undefined) {
        pp.BA.BA_HERBWRD = Number(pp.BA.BA_HERBWRD);
      }
      // BA_HUISNR must be integer per schema
      if (pp.BA.BA_HUISNR !== undefined) {
        pp.BA.BA_HUISNR = parseInt(String(pp.BA.BA_HUISNR), 10);
      }
      // BA_OPZPKVA must be integer per schema
      if (pp.BA.BA_OPZPKVA !== undefined) {
        pp.BA.BA_OPZPKVA = parseInt(String(pp.BA.BA_OPZPKVA), 10);
      }
    }

    // === PP.IP: clean corruption, ensure MP, convert to array (schema: type "array") ===
    if (pp.IP) {
      if (Array.isArray(pp.IP)) pp.IP = pp.IP[0] || {};
      // Legacy corrupted storage: pre-fix deepMerge object-spread arrays into {"0": ...}.
      if (pp.IP['0']) delete pp.IP['0'];
      if (pp.IP.IP_PPRC !== undefined) pp.IP.IP_PPRC = Number(pp.IP.IP_PPRC);
      if (!pp.IP.MP) pp.IP.MP = [];
      pp.IP = [pp.IP];
    }

    // === PP.EC: rebuild a clean ONE-row array (schema maxItems 1): only the selected row
    // (Smart Home replaces Mobiele elektronica) survives; the other row's price lives in the
    // screen's preview cache, not in the step data. ===
    if (pp.EC) {
      const selectedCode = selectedEcCode(clean.Supportsection?.Dekkingen || clean._uiState?.Dekkingen);
      const allRows = Array.isArray(pp.EC) ? pp.EC : [pp.EC];
      const selectedRows = allRows.filter((e: any) => e?.EC_CODE === selectedCode);
      // A document without the selected row is left alone (the probe request for the other
      // row's price, or a toggle whose recalculation is still in flight).
      const srcArr = selectedRows.length > 0 ? selectedRows : allRows;
      const cleanEc: any[] = [];
      // Mobiele elektronica (5450)
      const srcMobiel = srcArr.find((e: any) => e.EC_CODE === EC_CODE_MOBIEL);
      if (srcMobiel) {
        cleanEc.push(carryEcPremiumEcho({
          EC_CODE: EC_CODE_MOBIEL, EC_ENTREF: EC_CODE_MOBIEL,
          EC_PPRC: srcMobiel.EC_PPRC !== undefined ? Number(srcMobiel.EC_PPRC) : 20,
          EC_VERZSOM: srcMobiel.EC_VERZSOM ?? 25000,
          EC_ERB: srcMobiel.EC_ERB ?? 100,
          EC_GEBIED: srcMobiel.EC_GEBIED || 'W',
          EC_VOLGNUM: srcMobiel.EC_VOLGNUM ?? 1,
        }, srcMobiel));
      }
      // Smart Home (5470)
      const srcSmarthome = srcArr.find((e: any) => e.EC_CODE === EC_CODE_SMARTHOME);
      if (srcSmarthome) {
        cleanEc.push(carryEcPremiumEcho({
          EC_CODE: EC_CODE_SMARTHOME, EC_ENTREF: EC_CODE_SMARTHOME,
          EC_PPRC: srcSmarthome.EC_PPRC !== undefined ? Number(srcSmarthome.EC_PPRC) : 20,
          EC_VERZSOM: srcSmarthome.EC_VERZSOM ?? 3000,
          EC_ERB: srcSmarthome.EC_ERB ?? 100,
          EC_GEBIED: srcSmarthome.EC_GEBIED || 'W',
          EC_VOLGNUM: srcSmarthome.EC_VOLGNUM ?? 1,
        }, srcSmarthome));
      }
      pp.EC = cleanEc;
    }

    // === PP.VR whitelist (additionalProperties: false) ===
    if (pp.VR) {
      const allowedVR = new Set([
        'MP', 'VR_CODE', 'VR_ENTREF', 'VR_NTP', 'VR_PKRTVMP',
        'VR_PPRC', 'VR_PTSLTB', 'VR_TASS', 'VR_TKRT', 'VR_TPP', 'VR_TTOT',
        'VR_TTPPRV', 'VR_TTSLTB', 'VR_VOLGNUM',
      ]);
      for (const key of Object.keys(pp.VR)) {
        if (!allowedVR.has(key)) delete pp.VR[key];
      }
      if (pp.VR.VR_PPRC !== undefined) pp.VR.VR_PPRC = Number(pp.VR.VR_PPRC);
    }

    // === PP.BI: BI_VERZSOM must be at least 7500 ===
    if (pp.BI?.BI_VERZSOM !== undefined && pp.BI.BI_VERZSOM < 7500) {
      pp.BI.BI_VERZSOM = 7500;
    }

    // === Type enforcement: PPRC fields must be numbers ===
    for (const section of ['BI', 'GP', 'HU', 'KB', 'LS', 'OP']) {
      if (pp[section]?.[`${section}_PPRC`] !== undefined) {
        pp[section][`${section}_PPRC`] = Number(pp[section][`${section}_PPRC`]);
      }
    }

    // === PP-level type enforcement ===
    if (pp.PP_CDUUMND !== undefined) pp.PP_CDUUMND = Number(pp.PP_CDUUMND);
    if (pp.PP_HVVDAT !== undefined) pp.PP_HVVDAT = Number(pp.PP_HVVDAT);
    if (pp.PP_INGDAT !== undefined) pp.PP_INGDAT = Number(pp.PP_INGDAT);
    if (pp.PP_TARDAT !== undefined) pp.PP_TARDAT = Number(pp.PP_TARDAT);
    if (pp.PP_BETTERM !== undefined) pp.PP_BETTERM = Number(pp.PP_BETTERM);

    // === Required empty objects (additionalProperties: false, no properties) ===
    if (!pp.TP || typeof pp.TP !== 'object') pp.TP = {};
    if (!pp.VL || typeof pp.VL !== 'object') pp.VL = {};
    if (!pp.CL || !Array.isArray(pp.CL)) pp.CL = [];
    if (!pp.QS) pp.QS = { QA: [] };
    if (!pp.QS.QA) pp.QS.QA = [];
    // TP and VL must be empty objects (additionalProperties: false, no properties defined)
    for (const key of Object.keys(pp.TP)) delete pp.TP[key];
    for (const key of Object.keys(pp.VL)) delete pp.VL[key];
  }

  // === Contractdocument: only AL and PP allowed (additionalProperties: false) ===
  if (clean.Contractdocument) {
    const allowedCD = new Set(['AL', 'PP']);
    for (const key of Object.keys(clean.Contractdocument)) {
      if (!allowedCD.has(key)) delete clean.Contractdocument[key];
    }
  }

  // === Save Dekkingen to _uiState before removing Supportsection ===
  if (clean.Supportsection?.Dekkingen) {
    const dek = clean.Supportsection.Dekkingen;
    // Migrate (and drop) legacy key names FIRST so the _uiState copy never carries them
    migrateLegacyDekkingenKeys(dek);
    // Save Dekkingen values to _uiState (including Cyber/Verhuur for UI tracking)
    if (dek.Cyber !== undefined) clean._uiState.Dekkingen_Cyber = dek.Cyber;
    if (dek.Verhuur !== undefined) clean._uiState.Dekkingen_Verhuur = dek.Verhuur;
    // Save full Dekkingen to _uiState for coverage tracking across navigations
    clean._uiState.Dekkingen = { ...dek };
  }

  // === Remove non-schema root fields from all calls ===
  // Supportsection is kept in storage (BFF call sites strip it explicitly before sending).
  // Matches the PersonenAuto/Rechtsbijstand pattern: raw storage, strip only in payload.
  delete clean.Relatiedocument;
  delete clean._completed;
  delete clean._timestamp;

  return clean;
};

export { extractDefaults } from '../../../utils/extractDefaults';

export { deepMerge } from '../../../utils/deepMerge';
export const applyProductDefaults = (data: any, opts: ApplyInboedelDefaultsOptions): any => {
  // Get application name and base URL for AL fields
  const { appName, baseUrl, now } = opts;
  // Deep clone: this function mutates nested objects, and callers share/copy slices
  // between products — a shallow spread would corrupt the caller's data.
  const result = JSON.parse(JSON.stringify(data));

  // Ensure Contractdocument.PP structure exists
  if (!result.Contractdocument) {
    result.Contractdocument = {};
  }
  if (!result.Contractdocument.PP) {
    result.Contractdocument.PP = {};
  }

  // === Restore UI-only fields from _uiState ===
  // cleanInboedelForSchema preserves non-schema fields at root _uiState during saves.
  // Restore them here so the UI has access to these values.
  const uiState = result._uiState || {};

  // IB (Inboedelwaarde vaststelling - UI dropdown, not in PP schema)
  if (uiState.IB_INBVST && !result.Contractdocument.PP.IB?.IB_INBVST) {
    if (!result.Contractdocument.PP.IB) result.Contractdocument.PP.IB = {};
    result.Contractdocument.PP.IB.IB_INBVST = uiState.IB_INBVST;
  }

  // CY (Cyber coverage premiums - needed for price display, not in PP schema)
  if (uiState.CY && !result.Contractdocument.PP.CY) {
    result.Contractdocument.PP.CY = { ...uiState.CY };
  }

  // BA_OPPWON (surface area — in the BA whitelist and sent to the BFF; stash = backup)
  if (uiState.BA_OPPWON !== undefined) {
    if (!result.Contractdocument.PP.BA) result.Contractdocument.PP.BA = {};
    if (result.Contractdocument.PP.BA.BA_OPPWON === undefined) {
      result.Contractdocument.PP.BA.BA_OPPWON = uiState.BA_OPPWON;
    }
  }

  // BA_MONUMEN (monument check flag — in the BA whitelist and sent to the BFF; stash = backup)
  if (uiState.BA_MONUMEN !== undefined) {
    if (!result.Contractdocument.PP.BA) result.Contractdocument.PP.BA = {};
    if (result.Contractdocument.PP.BA.BA_MONUMEN === undefined) {
      result.Contractdocument.PP.BA.BA_MONUMEN = uiState.BA_MONUMEN;
    }
  }

  // BA_BOUWJR (construction year — in the BA whitelist and sent to the BFF; the stash
  // survives the round-trip through summary's BFF chain as a backup)
  if (uiState.BA_BOUWJR !== undefined) {
    if (!result.Contractdocument.PP.BA) result.Contractdocument.PP.BA = {};
    if (result.Contractdocument.PP.BA.BA_BOUWJR === undefined) {
      result.Contractdocument.PP.BA.BA_BOUWJR = uiState.BA_BOUWJR;
    }
  }

  // BA_HERBWRD (rebuild value - Infofolio or Glas-card input; rates the Midglas glass premium)
  if (uiState.BA_HERBWRD !== undefined) {
    if (!result.Contractdocument.PP.BA) result.Contractdocument.PP.BA = {};
    if (result.Contractdocument.PP.BA.BA_HERBWRD === undefined) {
      result.Contractdocument.PP.BA.BA_HERBWRD = uiState.BA_HERBWRD;
    }
  }

  result.Contractdocument.PP.PP_PPRC = 20;

  // Supportsection.Dekkingen is preserved in storage now, so existingData already carries
  // the user's selections through deepMerge. _uiState is only a fallback for legacy storage
  // (saved before Supportsection preservation) that has no Supportsection at all.
  if (!result.Supportsection) result.Supportsection = {};
  if (!result.Supportsection.Dekkingen) {
    result.Supportsection.Dekkingen = uiState.Dekkingen ? { ...uiState.Dekkingen } : {};
  }

  // === PP-level defaults ===

  // PP_TTOT - Total premium (initialized to 0, calculated dynamically)
  if (result.Contractdocument.PP.PP_TTOT === undefined) {
    result.Contractdocument.PP.PP_TTOT = 0;
  }

  // PP_CDUUMND - Contractduur in maanden (must be number per schema)
  if (!result.Contractdocument.PP.PP_CDUUMND) {
    result.Contractdocument.PP.PP_CDUUMND = 12;
  } else {
    result.Contractdocument.PP.PP_CDUUMND = Number(result.Contractdocument.PP.PP_CDUUMND);
  }

  // PP_BRANCHE - ADN branchecode (056 for household contents insurance)
  if (!result.Contractdocument.PP.PP_BRANCHE) {
    result.Contractdocument.PP.PP_BRANCHE = '056';
  }

  // PP_AFDDEFN - AFD definitienaam
  if (!result.Contractdocument.PP.PP_AFDDEFN) {
    result.Contractdocument.PP.PP_AFDDEFN = '056-R515-Inboedel_2024';
  }

  // PP_ADEFVRS - AFD definitieversienummer
  if (!result.Contractdocument.PP.PP_ADEFVRS) {
    result.Contractdocument.PP.PP_ADEFVRS = '004.01';
  }

  // PP_HANDACC - Handmatige acceptatie
  if (!result.Contractdocument.PP.PP_HANDACC) {
    result.Contractdocument.PP.PP_HANDACC = 'N';
  }

  // PP_STATUS - Status polis. The package endpoint (/productservice/package) only accepts
  // contracts with PP_STATUS in {7,8,9,10,14}; "8" is what the BFF calculate response returns
  // at offer stage. Seeding it here guarantees a valid status even when the slice is persisted
  // before a successful calculate (otherwise the package call rejects the whole document).
  if (!result.Contractdocument.PP.PP_STATUS) {
    result.Contractdocument.PP.PP_STATUS = '8';
  }

  // PP_EKST - Servicevergoeding (config-driven via prolongationFee)
  result.Contractdocument.PP.PP_EKST = 0;

  // PP_AFWAFSP not used for SAA - strip if present
  delete result.Contractdocument.PP.PP_AFWAFSP;

  // === BA (Building details) defaults ===

  if (!result.Contractdocument.PP.BA) {
    result.Contractdocument.PP.BA = {};
  }

  // Building wall type - removed test default, user must fill
  // BA_AARDBW should come from user input

  // Roof type - removed test default, user must fill
  // BA_AARDDAK should come from user input

  // Adjacent building type (Rhion R515 field)
  // BA_BDRFBEL should come from user input

  // Own home indicator (Rhion R515 field) - user must fill
  // BA_EIGENWO should come from user input

  // Permanent resident (Rhion R515 field) - user must fill
  // BA_PERMBEW should come from user input

  // Vaststelling herbouwwaarde (Reconstruction value determination) - required by R515
  // Derived from IB_INBVST: 'V' -> 'V', others -> 'E' (schema only allows E/V)
  {
    const ibVal = result.Contractdocument.PP.IB?.IB_INBVST || result._uiState?.IB_INBVST;
    result.Contractdocument.PP.BA.BA_HERBVST = ibVal || 'E';
  }

  // BA_VOLGNUM - address instance identifier
  if (result.Contractdocument.PP.BA.BA_VOLGNUM === undefined) {
    result.Contractdocument.PP.BA.BA_VOLGNUM = 1;
  }

  // BA_HUISNR must be integer per schema
  if (result.Contractdocument.PP.BA.BA_HUISNR !== undefined) {
    result.Contractdocument.PP.BA.BA_HUISNR = parseInt(String(result.Contractdocument.PP.BA.BA_HUISNR), 10);
  }

  // NOTE: BA_MONUMEN and BA_OPPWON are in the BA whitelist and are sent to the
  // BFF. They are also stashed at root._uiState as a backup restored on mount.

  // Postal code - removed test default, user must fill
  // BA_PCODE should come from user input

  // Type of dwelling - removed test default, user must fill
  // BA_TYPEWON should come from user input

  // Property usage - removed test default, user must fill
  // BA_WONING should come from user input

  // Note: IB (Inboedelwaarde) is NOT in the schema - IB_INBVST is tracked
  // in Supportsection.Divers or handled by BFF internally

  // === IP (Inboedel coverage) defaults ===
  // Note: Schema requires IP as array, but UI accesses as object throughout.
  // Conversion to array happens in ConfirmationScreen for the final request only.

  if (!result.Contractdocument.PP.IP) {
    result.Contractdocument.PP.IP = {};
  }
  // If BFF returned an array, unwrap to object for UI compatibility
  if (Array.isArray(result.Contractdocument.PP.IP)) {
    result.Contractdocument.PP.IP = result.Contractdocument.PP.IP[0] || {};
  }
  // Legacy corrupted storage: pre-fix deepMerge object-spread arrays into {"0": ...};
  // persisted data from before that fix can still carry the "0" key.
  if (result.Contractdocument.PP.IP['0']) delete result.Contractdocument.PP.IP['0'];

  // Product code - always All Risk (5016)
  result.Contractdocument.PP.IP.IP_CODE = '5016';
  result.Contractdocument.PP.IP.IP_ENTREF = '5016';

  // Prolongatie provisie percentage (PPRC) - must be number
  if (result.Contractdocument.PP.IP.IP_PPRC === undefined) {
    result.Contractdocument.PP.IP.IP_PPRC = 20;
  } else {
    result.Contractdocument.PP.IP.IP_PPRC = Number(result.Contractdocument.PP.IP.IP_PPRC);
  }

  // Verzekerde som - leave empty for user to fill in or infofolio to populate
  // No default value: infofolio provides IB_DAGWRDE when available, otherwise user enters manually

  // IP_VOLGNUM - coverage instance identifier
  if (result.Contractdocument.PP.IP.IP_VOLGNUM === undefined) {
    result.Contractdocument.PP.IP.IP_VOLGNUM = 1;
  }

  // IP_GARONDV - Garantie tegen onderverzekering: derived from IB_INBVST
  // 'M' (Waardebepaling maatschappij) -> 'J', others -> 'N'
  {
    const ibVal = result.Contractdocument.PP.IB?.IB_INBVST || result._uiState?.IB_INBVST;
    result.Contractdocument.PP.IP.IP_GARONDV = ibVal === 'M' ? 'J' : 'N';
  }

  // IP_ERB - Eigen risico: keep a valid own-risk choice (100/300/500), else default 100
  if (![100, 300, 500].includes(result.Contractdocument.PP.IP.IP_ERB)) {
    result.Contractdocument.PP.IP.IP_ERB = 100;
  }

  // IP_TAXVRT - Taxatie door VRT-taxateur, derived from IB_INBVST selection
  // IB_INBVST "V" = Vaste taxatie -> IP_TAXVRT "J", otherwise "N"
  const ibInbvst = result.Contractdocument.PP.IB?.IB_INBVST || result._uiState?.IB_INBVST;
  result.Contractdocument.PP.IP.IP_TAXVRT = ibInbvst === 'V' ? 'J' : 'N';

  // === VP (Person details) - schema only allows VP_GEBDAT and VP_PARZAKC ===

  if (!result.Contractdocument.PP.VP) {
    result.Contractdocument.PP.VP = {};
  }

  // Whitelist VP to only schema-allowed fields
  const vpBeforeWhitelist = result.Contractdocument.PP.VP;
  const vpGebdat = result.Contractdocument.PP.VP.VP_GEBDAT;
  const vpParzakc = result.Contractdocument.PP.VP.VP_PARZAKC || 'P';
  result.Contractdocument.PP.VP = {
    VP_PARZAKC: vpParzakc,
    ...(vpGebdat !== undefined ? { VP_GEBDAT: Number(vpGebdat) } : {}),
  };

  for (const f of VP_ADDRESS_UI_FIELDS) {
    const v = vpBeforeWhitelist?.[f] !== undefined && vpBeforeWhitelist[f] !== ''
      ? vpBeforeWhitelist[f]
      : uiState[f];
    if (v !== undefined) result.Contractdocument.PP.VP[f] = v;
  }

  // === BI (Bijzondere inboedel) defaults ===

  if (!result.Contractdocument.PP.BI) {
    result.Contractdocument.PP.BI = {};
  }

  // Product code
  if (!result.Contractdocument.PP.BI.BI_CODE) {
    result.Contractdocument.PP.BI.BI_CODE = '5045';
  }

  // Entity reference (must match CODE)
  if (!result.Contractdocument.PP.BI.BI_ENTREF) {
    result.Contractdocument.PP.BI.BI_ENTREF = '5045';
  }

  // MP array (required, can be empty)
  if (!result.Contractdocument.PP.BI.MP) {
    result.Contractdocument.PP.BI.MP = [];
  }

  // Prolongatie provisie percentage (PPRC) - must be number
  if (result.Contractdocument.PP.BI.BI_PPRC === undefined) {
    result.Contractdocument.PP.BI.BI_PPRC = 20;
  } else {
    result.Contractdocument.PP.BI.BI_PPRC = Number(result.Contractdocument.PP.BI.BI_PPRC);
  }

  // BI_VOLGNUM - coverage instance identifier
  if (result.Contractdocument.PP.BI.BI_VOLGNUM === undefined) {
    result.Contractdocument.PP.BI.BI_VOLGNUM = 1;
  }

  // Verzekerde som buitenhuis (required) - must be at least 7500
  if (!result.Contractdocument.PP.BI.BI_VERZSOM || result.Contractdocument.PP.BI.BI_VERZSOM < 7500) {
    result.Contractdocument.PP.BI.BI_VERZSOM = 7500;
  }

  // Eigen risico bedrag buitenhuis (required) - valid values: 100.00, 300.00, 500.00
  // FORCE to 100 if not one of the valid values
  const validBiErb = [100, 300, 500];
  if (!validBiErb.includes(result.Contractdocument.PP.BI.BI_ERB)) {
    result.Contractdocument.PP.BI.BI_ERB = 100;
  }

  // === LS (Lijfsieraden/Jewelry) defaults ===

  if (!result.Contractdocument.PP.LS) {
    result.Contractdocument.PP.LS = {};
  }

  // Product code
  if (!result.Contractdocument.PP.LS.LS_CODE) {
    result.Contractdocument.PP.LS.LS_CODE = '5950';
  }

  // Entity reference (must match CODE)
  if (!result.Contractdocument.PP.LS.LS_ENTREF) {
    result.Contractdocument.PP.LS.LS_ENTREF = '5950';
  }

  // Prolongatie provisie percentage (PPRC) - must be number
  if (result.Contractdocument.PP.LS.LS_PPRC === undefined) {
    result.Contractdocument.PP.LS.LS_PPRC = 20;
  } else {
    result.Contractdocument.PP.LS.LS_PPRC = Number(result.Contractdocument.PP.LS.LS_PPRC);
  }

  // Verzekerde som lijfsieraden — no default, user must fill in

  // === HU (Huurdersbelang/Tenant's interest) defaults ===

  if (!result.Contractdocument.PP.HU) {
    result.Contractdocument.PP.HU = {};
  }

  // Product code
  if (!result.Contractdocument.PP.HU.HU_CODE) {
    result.Contractdocument.PP.HU.HU_CODE = '5970';
  }

  // Entity reference (must match CODE)
  if (!result.Contractdocument.PP.HU.HU_ENTREF) {
    result.Contractdocument.PP.HU.HU_ENTREF = '5970';
  }

  // Prolongatie provisie percentage (PPRC) - must be number
  if (result.Contractdocument.PP.HU.HU_PPRC === undefined) {
    result.Contractdocument.PP.HU.HU_PPRC = 20;
  } else {
    result.Contractdocument.PP.HU.HU_PPRC = Number(result.Contractdocument.PP.HU.HU_PPRC);
  }

  // Verzekerde som huurdersbelang — no default, user must fill in

  // === KB (Kostbare bezittingen/Valuable possessions) defaults ===

  if (!result.Contractdocument.PP.KB) {
    result.Contractdocument.PP.KB = {};
  }

  // Product code
  if (!result.Contractdocument.PP.KB.KB_CODE) {
    result.Contractdocument.PP.KB.KB_CODE = '5302';
  }

  // Entity reference (must match CODE)
  if (!result.Contractdocument.PP.KB.KB_ENTREF) {
    result.Contractdocument.PP.KB.KB_ENTREF = '5302';
  }

  // Prolongatie provisie percentage (PPRC) - must be number
  if (result.Contractdocument.PP.KB.KB_PPRC === undefined) {
    result.Contractdocument.PP.KB.KB_PPRC = 20;
  } else {
    result.Contractdocument.PP.KB.KB_PPRC = Number(result.Contractdocument.PP.KB.KB_PPRC);
  }

  // KB_VOLGNUM - coverage instance identifier
  if (result.Contractdocument.PP.KB.KB_VOLGNUM === undefined) {
    result.Contractdocument.PP.KB.KB_VOLGNUM = 1;
  }

  // Verzekerde som bijzondere bezittingen — no default, user must fill in

  // === EC (Elektronica/Electronics) defaults ===
  // ONE row (schema maxItems 1): Smart Home (5470) when selected, else Mobiele elektronica
  // (5450). Built fresh to avoid corruption from nested index keys; the existing row of the
  // same code keeps its premium echo.
  const existingEcArr = Array.isArray(result.Contractdocument.PP.EC)
    ? result.Contractdocument.PP.EC
    : result.Contractdocument.PP.EC ? [result.Contractdocument.PP.EC] : [];
  const ecDekkingen = result.Supportsection?.Dekkingen || result._uiState?.Dekkingen;
  const ecCode = ecDekkingen?.Smarthome === 'J' ? EC_CODE_SMARTHOME : EC_CODE_MOBIEL;
  const existEc = existingEcArr.find((e: any) => e.EC_CODE === ecCode);
  result.Contractdocument.PP.EC = [
    carryEcPremiumEcho({
      EC_CODE: ecCode,
      EC_ENTREF: ecCode,
      EC_PPRC: existEc?.EC_PPRC !== undefined ? Number(existEc.EC_PPRC) : 20,
      EC_VERZSOM: (existEc?.EC_VERZSOM && existEc.EC_VERZSOM > 0) ? existEc.EC_VERZSOM : EC_DEFAULT_SUM[ecCode],
      EC_ERB: [100, 300, 500].includes(existEc?.EC_ERB) ? existEc.EC_ERB : 100,
      EC_VOLGNUM: existEc?.EC_VOLGNUM ?? 1,
    }, existEc),
  ];

  // === GP (Glas product) defaults ===

  if (!result.Contractdocument.PP.GP) {
    result.Contractdocument.PP.GP = {};
  }

  // Product code
  if (!result.Contractdocument.PP.GP.GP_CODE) {
    result.Contractdocument.PP.GP.GP_CODE = '5021';
  }

  // Entity reference (must match CODE)
  if (!result.Contractdocument.PP.GP.GP_ENTREF) {
    result.Contractdocument.PP.GP.GP_ENTREF = '5021';
  }

  // Maatschappij-aanduiding routing GP to the Midglas tariff
  if (!result.Contractdocument.PP.GP.GP_MYAAND) {
    result.Contractdocument.PP.GP.GP_MYAAND = GLASS_MYAAND;
  }

  // Prolongatie provisie percentage (PPRC) - must be number
  if (result.Contractdocument.PP.GP.GP_PPRC === undefined) {
    result.Contractdocument.PP.GP.GP_PPRC = 20;
  } else {
    result.Contractdocument.PP.GP.GP_PPRC = Number(result.Contractdocument.PP.GP.GP_PPRC);
  }

  // GP_VOLGNUM - coverage instance identifier
  if (result.Contractdocument.PP.GP.GP_VOLGNUM === undefined) {
    result.Contractdocument.PP.GP.GP_VOLGNUM = 1;
  }

  // === CL (Clauses) defaults ===

  if (!result.Contractdocument.PP.CL) {
    result.Contractdocument.PP.CL = [];
  }

  // === VE (Verzekeraar) defaults ===

  if (!result.Contractdocument.PP.VE) {
    result.Contractdocument.PP.VE = {};
  }

  // VE_MYAAND for Inboedel SAA
  if (!result.Contractdocument.PP.VE.VE_MYAAND) {
    result.Contractdocument.PP.VE.VE_MYAAND = 'K781';
  }

  // === VL defaults ===

  if (!result.Contractdocument.PP.VL) {
    result.Contractdocument.PP.VL = {};
  }

  // === VW array defaults (minItems: 1) ===

  if (!result.Contractdocument.PP.VW || result.Contractdocument.PP.VW.length === 0) {
    result.Contractdocument.PP.VW = [{}];
  }

  // === TP (empty object required) ===

  if (!result.Contractdocument.PP.TP) {
    result.Contractdocument.PP.TP = {};
  }

  // === OP (Opstal/Opruimingskosten product) - required by schema ===

  if (!result.Contractdocument.PP.OP) {
    result.Contractdocument.PP.OP = { OP_CODE: '5010' };
  }

  // === RI (Risico-informatie) - required by schema ===

  if (!result.Contractdocument.PP.RI) {
    result.Contractdocument.PP.RI = {
      RI_CGGDA: 'N',
      RI_EGD: 'N',
      RI_OGB: 'N',
      RI_OVRMED: 'N',
      RI_POLJUS: 'N',
      RI_STVOOP: 'N',
    };
  }

  // === QS (Acceptatievragen) - required by schema ===

  if (!result.Contractdocument.PP.QS) {
    result.Contractdocument.PP.QS = { QA: [] };
  }

  // === PP_HVVDAT (Hoofdvervaldatum) - first day of current month, next year ===
  if (!result.Contractdocument.PP.PP_HVVDAT) {
    const hvvNow = now;
    const hvvMonth = String(hvvNow.getMonth() + 1).padStart(2, '0');
    result.Contractdocument.PP.PP_HVVDAT = Number(`${hvvNow.getFullYear() + 1}${hvvMonth}01`);
  }

  // === CY (Cyber) defaults ===

  if (!result.Contractdocument.PP.CY) {
    result.Contractdocument.PP.CY = {};
  }

  // Only set CY fields if Cyber is selected
  if (result.Supportsection?.Dekkingen?.Cyber === 'J') {
    if (!result.Contractdocument.PP.CY.CY_CODE) {
      result.Contractdocument.PP.CY.CY_CODE = '5407';
    }
    if (!result.Contractdocument.PP.CY.CY_ENTREF) {
      result.Contractdocument.PP.CY.CY_ENTREF = '5407';
    }
    if (!result.Contractdocument.PP.CY.CY_PPRC && result.Contractdocument.PP.CY.CY_PPRC !== 0) {
      result.Contractdocument.PP.CY.CY_PPRC = 20;
    }
  }

  // === AL (empty object required) ===

  if (!result.Contractdocument.AL) {
    result.Contractdocument.AL = {};
  }

  // Always ensure AL_APPLNM and AL_URLNAAM are set
  result.Contractdocument.AL = {
    AL_APPLNM: appName,
    AL_URLNAAM: baseUrl,
    ...result.Contractdocument.AL
  };

  // === Supportsection with Dekkingen ===

  if (!result.Supportsection) {
    result.Supportsection = {};
  }
  if (!result.Supportsection.Dekkingen) {
    result.Supportsection.Dekkingen = {};
  }

  // Migrate old Dekkingen key names to schema-compliant names (always deletes old keys)
  const dek = result.Supportsection.Dekkingen;
  migrateLegacyDekkingenKeys(dek);

  // Hoofddekking - always All Risk
  dek.Hoofddekking = 'Allrisk';

  // Glas (optional, default N)
  if (!dek.Glas) {
    dek.Glas = 'N';
  }

  // Lijfsieraden (optional, default N)
  if (!dek.Lijfsieraden) {
    dek.Lijfsieraden = 'N';
  }

  // Buitenshuis - always included
  dek.Buitenshuis = 'J';

  // Mobieleelektronica - always included
  dek.Mobieleelektronica = 'J';

  // Smarthome (optional, default N)
  if (!dek.Smarthome) {
    dek.Smarthome = 'N';
  }

  // Eigenaarsbelang (optional, default N)
  if (!dek.Eigenaarsbelang) {
    dek.Eigenaarsbelang = 'N';
  }

  // Huurdersbelang (optional, default N)
  if (!dek.Huurdersbelang) {
    dek.Huurdersbelang = 'N';
  }

  // Bijzonderebezittingen (optional, default N)
  if (!dek.Bijzonderebezittingen) {
    dek.Bijzonderebezittingen = 'N';
  }

  // GA (Onbeperkte nieuwwaarde) and VR (Verhuur) - no longer offered, clean up
  delete result.Contractdocument.PP.GA;
  delete result.Contractdocument.PP.VR;

  // === Supportsection.Divers ===
  if (!result.Supportsection.Divers) {
    result.Supportsection.Divers = {};
  }
  // RisicoAdresGelijk (default J - risk address same as correspondence address)
  if (!result.Supportsection.Divers.RisicoAdresGelijk) {
    result.Supportsection.Divers.RisicoAdresGelijk = 'J';
  }

  // === Ensure Relatiedocument exists (populated by cleanInboedelForSchema on save) ===
  if (!result.Relatiedocument) {
    result.Relatiedocument = {};
  }
  if (!result.Relatiedocument.VP) {
    result.Relatiedocument.VP = {};
  }

  return result;
};
export const linkRelationData = (productData: any, relationData: any, opts: { now: Date }): any => {
  if (!relationData || !relationData.clientInfo) {
    return productData;
  }

  const result = JSON.parse(JSON.stringify(productData));

  // Ensure VP structure exists
  if (!result.Contractdocument.PP.VP) {
    result.Contractdocument.PP.VP = {};
  }

  // Ensure BA structure exists for address fields
  if (!result.Contractdocument.PP.BA) {
    result.Contractdocument.PP.BA = {};
  }

  // Ensure Relatiedocument.VP structure exists
  if (!result.Relatiedocument) {
    result.Relatiedocument = {};
  }
  if (!result.Relatiedocument.VP) {
    result.Relatiedocument.VP = {};
  }

  // Link client information fields to Relatiedocument.VP only
  // PP.VP only allows VP_GEBDAT and VP_PARZAKC per schema
  if (relationData.clientInfo.lastName) {
    result.Relatiedocument.VP.VP_ANAAM = relationData.clientInfo.lastName;
  }
  // VP_AANHEFC / VP_TITULAT — zero-padded 5-char gender codes ('00001' = Dhr., '00002' = Mevr.).
  // Explicit clientInfo.salutationCode / clientInfo.titulature take precedence.
  const salutationDerived = relationData.clientInfo.gender === 'M' ? '00001'
    : relationData.clientInfo.gender === 'V' ? '00002' : undefined;
  if (relationData.clientInfo.salutationCode) {
    result.Relatiedocument.VP.VP_AANHEFC = relationData.clientInfo.salutationCode;
  } else if (salutationDerived) {
    result.Relatiedocument.VP.VP_AANHEFC = salutationDerived;
  }
  if (relationData.clientInfo.titulature) {
    result.Relatiedocument.VP.VP_TITULAT = relationData.clientInfo.titulature;
  } else if (salutationDerived) {
    result.Relatiedocument.VP.VP_TITULAT = salutationDerived;
  }

  // Link address fields from clientInfo to BA (Beschrijving Adres)
  // Only seed empty BA fields — clientInfo is the *original* address from the
  // client-info screen and never updates after that, so on remount it must not
  // overwrite an address the user has since edited on this product (the new value
  // lives in step data / sharedAddress).
  // Note: Relatiedocument.VP should NOT have address fields when RisicoAdresGelijk = "J"
  if (relationData.clientInfo.street && !result.Contractdocument.PP.BA.BA_STRAAT) {
    result.Contractdocument.PP.BA.BA_STRAAT = relationData.clientInfo.street;
  }

  if (relationData.clientInfo.houseNumber && !result.Contractdocument.PP.BA.BA_HUISNR) {
    result.Contractdocument.PP.BA.BA_HUISNR = parseInt(String(relationData.clientInfo.houseNumber), 10);
  }

  if (relationData.clientInfo.houseNumberAddition && !result.Contractdocument.PP.BA.BA_TOEVOEG) {
    result.Contractdocument.PP.BA.BA_TOEVOEG = relationData.clientInfo.houseNumberAddition;
  }

  if (relationData.clientInfo.postalCode && !result.Contractdocument.PP.BA.BA_PCODE) {
    result.Contractdocument.PP.BA.BA_PCODE = relationData.clientInfo.postalCode;
  }

  if (relationData.clientInfo.city && !result.Contractdocument.PP.BA.BA_PLAATS) {
    result.Contractdocument.PP.BA.BA_PLAATS = relationData.clientInfo.city;
  }

  // Date of birth arrives as DD-MM-YYYY from client information, but ISO YYYY-MM-DD
  // occurs too (e.g. date inputs) — detect the format instead of assuming.
  if (relationData.clientInfo.dateOfBirth) {
    const rawDob = String(relationData.clientInfo.dateOfBirth);
    let dateStr = rawDob.replace(/-/g, '');
    if (/^\d{4}-\d{2}-\d{2}$/.test(rawDob)) {
      // ISO YYYY-MM-DD: already in YYYYMMDD order after stripping dashes
    } else if (/^\d{2}-\d{2}-\d{4}$/.test(rawDob) && dateStr.length === 8) {
      // Convert DDMMYYYY to YYYYMMDD
      const day = dateStr.substring(0, 2);
      const month = dateStr.substring(2, 4);
      const year = dateStr.substring(4, 8);
      dateStr = year + month + day;
    } else if (dateStr.length === 8) {
      // Unseparated 8-digit input: keep the historical DDMMYYYY interpretation
      const day = dateStr.substring(0, 2);
      const month = dateStr.substring(2, 4);
      const year = dateStr.substring(4, 8);
      dateStr = year + month + day;
    }
    const dateNum = Number(dateStr);
    const year = Math.floor(dateNum / 10000);

    // Validate the year is sensible (between 1900 and current year)
    const currentYear = opts.now.getFullYear();
    const isValidYear = year >= 1900 && year <= currentYear;

    if (!isNaN(dateNum) && dateStr.length === 8 && isValidYear) {
      result.Contractdocument.PP.VP.VP_GEBDAT = dateNum;
      result.Relatiedocument.VP.VP_GEBDAT = dateNum;
    } else {
      result.Contractdocument.PP.VP.VP_GEBDAT = 19900101;
      result.Relatiedocument.VP.VP_GEBDAT = 19900101;
    }
  } else {
    result.Contractdocument.PP.VP.VP_GEBDAT = 19900101;
    result.Relatiedocument.VP.VP_GEBDAT = 19900101;
  }

  return result;
};
export const linkWoonhuisAddress = (inboedelData: any, woonhuisData: any): any => {
  if (!woonhuisData) {
    return inboedelData;
  }

  const result = JSON.parse(JSON.stringify(inboedelData));

  // Get existing Inboedel BA data
  const inboedelBA = result?.Contractdocument?.PP?.BA;
  const woonhuisBA = woonhuisData?.Contractdocument?.PP?.BA;

  // Only copy from Woonhuis if it has address data and Inboedel doesn't have its own
  if (woonhuisBA && woonhuisBA.BA_PCODE) {
    // Ensure BA structure exists
    if (!result.Contractdocument.PP.BA) {
      result.Contractdocument.PP.BA = {};
    }

    // Copy address fields from Woonhuis if Inboedel doesn't have them
    if (!inboedelBA?.BA_PCODE && woonhuisBA.BA_PCODE) result.Contractdocument.PP.BA.BA_PCODE = woonhuisBA.BA_PCODE;
    if (!inboedelBA?.BA_HUISNR && woonhuisBA.BA_HUISNR) result.Contractdocument.PP.BA.BA_HUISNR = woonhuisBA.BA_HUISNR;
    if (!inboedelBA?.BA_TOEVOEG && woonhuisBA.BA_TOEVOEG) result.Contractdocument.PP.BA.BA_TOEVOEG = woonhuisBA.BA_TOEVOEG;
    if (!inboedelBA?.BA_STRAAT && woonhuisBA.BA_STRAAT) result.Contractdocument.PP.BA.BA_STRAAT = woonhuisBA.BA_STRAAT;
    if (!inboedelBA?.BA_PLAATS && woonhuisBA.BA_PLAATS) result.Contractdocument.PP.BA.BA_PLAATS = woonhuisBA.BA_PLAATS;

    // Copy building details from Woonhuis if Inboedel doesn't have them
    if (!inboedelBA?.BA_TYPEWON && woonhuisBA.BA_TYPEWON) result.Contractdocument.PP.BA.BA_TYPEWON = woonhuisBA.BA_TYPEWON;
    if (!inboedelBA?.BA_AARDBW && woonhuisBA.BA_AARDBW) result.Contractdocument.PP.BA.BA_AARDBW = woonhuisBA.BA_AARDBW;
    if (!inboedelBA?.BA_AARDDAK && woonhuisBA.BA_AARDDAK) result.Contractdocument.PP.BA.BA_AARDDAK = woonhuisBA.BA_AARDDAK;
    if (!inboedelBA?.BA_WONING && woonhuisBA.BA_WONING) result.Contractdocument.PP.BA.BA_WONING = woonhuisBA.BA_WONING;
    if (!inboedelBA?.BA_BOUWJR && woonhuisBA.BA_BOUWJR) result.Contractdocument.PP.BA.BA_BOUWJR = woonhuisBA.BA_BOUWJR;
    if (!inboedelBA?.BA_OPPWON && woonhuisBA.BA_OPPWON) result.Contractdocument.PP.BA.BA_OPPWON = woonhuisBA.BA_OPPWON;
    if (!inboedelBA?.BA_EIGENWO && woonhuisBA.BA_EIGENWO) result.Contractdocument.PP.BA.BA_EIGENWO = woonhuisBA.BA_EIGENWO;
    if (!inboedelBA?.BA_PERMBEW && woonhuisBA.BA_PERMBEW) result.Contractdocument.PP.BA.BA_PERMBEW = woonhuisBA.BA_PERMBEW;
    if (!inboedelBA?.BA_BDRFBEL && woonhuisBA.BA_BDRFBEL) result.Contractdocument.PP.BA.BA_BDRFBEL = woonhuisBA.BA_BDRFBEL;
    if (!inboedelBA?.BA_OPZPKVA && woonhuisBA.BA_OPZPKVA) result.Contractdocument.PP.BA.BA_OPZPKVA = woonhuisBA.BA_OPZPKVA;
    if (!inboedelBA?.BA_BGM2 && woonhuisBA.BA_BGM2) result.Contractdocument.PP.BA.BA_BGM2 = woonhuisBA.BA_BGM2;
    if (!inboedelBA?.BA_TOPPBGB && woonhuisBA.BA_TOPPBGB) result.Contractdocument.PP.BA.BA_TOPPBGB = woonhuisBA.BA_TOPPBGB;
    // Sync BA_HERBVST from woonhuis to IB_INBVST (inboedel derives BA_HERBVST from IB_INBVST)
    if (woonhuisBA.BA_HERBVST) {
      if (!result.Contractdocument.PP.IB) result.Contractdocument.PP.IB = {};
      if (!result.Contractdocument.PP.IB.IB_INBVST) {
        result.Contractdocument.PP.IB.IB_INBVST = woonhuisBA.BA_HERBVST;
      }
    }

    // Sync the person's address into Relatiedocument.VP (the relation person). NOT into
    // Contractdocument.PP.VP — that section's whitelist (VP_GEBDAT/VP_PARZAKC only) strips
    // address fields on the next clean, so writing them there was a silent no-op.
    if (!result.Relatiedocument) result.Relatiedocument = {};
    if (!result.Relatiedocument.VP) result.Relatiedocument.VP = {};
    const relVP = result.Relatiedocument.VP;
    if (!relVP.VP_STRAAT && woonhuisBA.BA_STRAAT) relVP.VP_STRAAT = woonhuisBA.BA_STRAAT;
    if (!relVP.VP_HUISNR && woonhuisBA.BA_HUISNR) relVP.VP_HUISNR = woonhuisBA.BA_HUISNR;
    if (!relVP.VP_TOEVOEG && woonhuisBA.BA_TOEVOEG) relVP.VP_TOEVOEG = woonhuisBA.BA_TOEVOEG;
    if (!relVP.VP_PCODE && woonhuisBA.BA_PCODE) relVP.VP_PCODE = woonhuisBA.BA_PCODE;
    if (!relVP.VP_PLAATS && woonhuisBA.BA_PLAATS) relVP.VP_PLAATS = woonhuisBA.BA_PLAATS;
  }

  // Sync Gezinssamenstelling from Woonhuis if Inboedel doesn't have it
  const woonhuisOP = woonhuisData?.Contractdocument?.PP?.OP;
  const inboedelIP = result?.Contractdocument?.PP?.IP;
  if (woonhuisOP?.OP_GSS && !inboedelIP?.IP_GSS) {
    if (!result.Contractdocument.PP.IP) {
      result.Contractdocument.PP.IP = {};
    }
    result.Contractdocument.PP.IP.IP_GSS = woonhuisOP.OP_GSS;
  }

  // Sync RisicoAdresGelijk from Woonhuis
  const woonhuisRisicoAdres = woonhuisData?.Supportsection?.Divers?.RisicoAdresGelijk;
  if (woonhuisRisicoAdres) {
    if (!result.Supportsection) result.Supportsection = {};
    if (!result.Supportsection.Divers) result.Supportsection.Divers = {};
    result.Supportsection.Divers.RisicoAdresGelijk = woonhuisRisicoAdres;
  }

  return result;
};
